import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Car, Factory, Landmark, HeartPulse, ShoppingBag, Hotel } from 'lucide-react';
import { CLIENTS } from '@/constants/testIds';

const INDUSTRIES = [
    { id: 'technology', label: 'Technology', icon: Cpu },
    { id: 'automotive', label: 'Automotive', icon: Car },
    { id: 'manufacturing', label: 'Manufacturing', icon: Factory },
    { id: 'bfsi', label: 'BFSI', icon: Landmark },
    { id: 'healthcare', label: 'Healthcare', icon: HeartPulse },
    { id: 'retail', label: 'Retail', icon: ShoppingBag },
    { id: 'hospitality', label: 'Hospitality', icon: Hotel },
];

export const Clients = () => {
    return (
        <section id="clients" data-testid={CLIENTS.root} className="relative py-24 sm:py-32 bg-neutral-950 border-y border-white/5">
            <div className="container-lux">
                <div className="text-center mb-16">
                    <div className="flex items-center justify-center gap-4 mb-6">
                        <div className="w-8 h-px bg-gold" />
                        <span className="eyebrow">Clients</span>
                        <span className="text-white/30 text-[10px] tracking-[0.3em]">06</span>
                        <div className="w-8 h-px bg-gold" />
                    </div>
                    <h2 className="font-display font-light text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        Trusted by Leading <br />
                        <span className="italic text-gold font-extralight">Brands Across Industries</span>
                    </h2>
                </div>

                {/* Industries served */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 border border-white/10 mb-14">
                    {INDUSTRIES.map((ind, i) => {
                        const Icon = ind.icon;
                        return (
                            <motion.div
                                key={ind.id}
                                data-testid={CLIENTS.logo(ind.id)}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className={`h-32 flex flex-col items-center justify-center gap-3 relative group hover:bg-black/60 transition-colors ${
                                    i < INDUSTRIES.length - 1 ? 'lg:border-r border-white/10' : ''
                                } ${(i + 1) % 2 !== 0 ? 'sm:border-r' : ''} ${(i + 1) % 3 !== 0 ? 'sm:border-r' : ''} ${i < 4 ? 'border-b sm:border-b lg:border-b-0' : ''}`}
                            >
                                <Icon size={26} strokeWidth={1.2} className="text-gold group-hover:scale-110 transition-transform" />
                                <span className="font-display text-white/70 text-[11px] tracking-[0.25em] uppercase group-hover:text-gold transition-colors">
                                    {ind.label}
                                </span>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Industries We Serve — placeholder until client logos are uploaded */}
                <div className="border border-white/10 py-14 px-8 text-center bg-black/40">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <div className="w-6 h-px bg-gold" />
                        <div className="text-gold text-[10px] tracking-[0.3em] uppercase">Industries We Serve</div>
                        <div className="w-6 h-px bg-gold" />
                    </div>
                    <p className="text-white/60 text-sm max-w-2xl mx-auto font-light leading-relaxed">
                        Delivering premium experiences across Technology, Automotive, Manufacturing, BFSI, Healthcare, Retail and Hospitality — trusted by leadership teams to bring their most ambitious moments to life.
                    </p>
                </div>

                <div className="mt-10 text-center text-white/50 text-xs tracking-[0.3em] uppercase font-light">
                    Delivering premium experiences across sectors
                </div>
            </div>
        </section>
    );
};

export default Clients;
